/**
 * ContractListPage — Phase 6a.10 (introduced) / 6a.11 (calculate wiring).
 *
 * Lists every contract slot in the active engagement with its status
 * indicator. Supports append-only adding (`+ Yeni Sözleşme Ekle`) and
 * surfaces the engagement Calculate button (disabled until ALL slots
 * reach `filled`).
 *
 * Phase 6a.11 additions (Spec §21.7.9):
 *   • Calculate button is wired to `useCalculateEngagement` → backend.
 *   • `<CalculateLoadingOverlay>` renders during in-flight calculation
 *     with a non-dismissable spinner and a Cancel button that calls
 *     `AbortController.abort()` on the request.
 *   • A rejection banner appears above the list when the engagement
 *     has `lastCalculateError` — it auto-clears when the user edits
 *     all rejected slots (auto-clear lives in `useEngagementContractDraft`).
 *   • Navigation guard via popstate listener prompts confirm before
 *     leaving while a calculation is in flight (`useBlocker` isn't
 *     available — see deviation note below).
 *
 * Design update — the slot list is now a shadcn table with a checkbox
 * column (select-all + per-row) and a bulk-delete action. Single-row
 * Edit/Delete are unchanged; bulk delete reuses historyStorage.deleteContract
 * descending-index so the existing splice/reindex logic stays authoritative.
 */

import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { toast } from "sonner";

import { PageContainer } from "@/components/layout/PageContainer";
import { Admonition } from "@/components/ui/admonition";
import { ContractsImportSection } from "@/features/contract-form/import/ContractsImportSection";
import { FxSyncAuditAccordion } from "@/features/fx-sync/FxSyncAuditAccordion";
import { MagicSyncButton } from "@/features/fx-sync/MagicSyncButton";
import type { FxSyncPlan } from "@/features/fx-sync/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatIsoToDisplay } from "@/components/ui/date-picker.utils";
import { formatNumber, formatPercent } from "@/lib/format";
import { CalculateLoadingOverlay } from "@/features/engagement/components/CalculateLoadingOverlay";
import { ContractStatusDot } from "@/features/engagement/components/ContractStatusDot";
import { DeleteConfirmDialog } from "@/features/engagement/components/DeleteConfirmDialog";
import { deriveContractStatus } from "@/features/engagement/contract-status";
import { displayNameFor } from "@/features/engagement/displayName";
import { transitionToStale } from "@/features/engagement/engagement-state";
import { useCalculateEngagement } from "@/features/engagement/useCalculateEngagement";
import { useEngagementAccess } from "@/features/engagement/useEngagementAccess";
import { useHistoryStorage } from "@/services";
import type { EngagementHistoryEntry } from "@/types";

export function ContractListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const historyStorage = useHistoryStorage();
  // SPEC-SHARE-B1 — viewers (shared, role=viewer) get a read-only surface:
  // every write control is hidden or disabled. Fail-safe: `readOnly` is
  // TRUE while access is still loading.
  const { readOnly } = useEngagementAccess(id);
  const [engagement, setEngagement] =
    React.useState<EngagementHistoryEntry | null>(null);
  const [loadState, setLoadState] = React.useState<"loading" | "loaded" | "missing">(
    "loading",
  );
  // Phase 6e.4.2 — the Magic Button (relocated from the engagement
  // form to here) returns the matcher's plan after a successful
  // sync. We hold the most recent one so the FxSyncAuditAccordion
  // can render per-contract detail.
  const [latestFxSyncPlan, setLatestFxSyncPlan] =
    React.useState<FxSyncPlan | null>(null);

  // ── Bulk selection + bulk delete ────────────────────────────────────
  // Selection is by slot index. Any mutation of the contracts array
  // (add / single delete / bulk delete) clears the selection because the
  // indices shift after a splice.
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);

  // ── Phase 6e.4.2-hotfix-2 — hard-delete contract state (Bug D) ──────
  // Replaces the Phase 6a.22a soft-delete + 5s undo banner that left
  // ghost slots behind when the user took other actions during the
  // window. Hard delete is atomic: confirm → splice → toast.
  const [pendingDeleteIndex, setPendingDeleteIndex] = React.useState<
    number | null
  >(null);

  // Hydrate from localStorage.
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!id) {
        if (!cancelled) setLoadState("missing");
        return;
      }
      const e = await historyStorage.getEngagement(id);
      if (cancelled) return;
      if (!e) {
        setLoadState("missing");
        return;
      }
      setEngagement(e);
      setLoadState("loaded");
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, historyStorage]);

  // Redirect to /history if engagement not found.
  React.useEffect(() => {
    if (loadState === "missing") {
      navigate("/history", { replace: true });
    }
  }, [loadState, navigate]);

  async function handleAddContract() {
    if (!engagement) return;
    const nextContracts = [...engagement.contracts, null];
    const nextDirty = [...(engagement.contractsDirty ?? []), false];
    let updated: EngagementHistoryEntry = {
      ...engagement,
      contracts: nextContracts,
      contractsDirty: nextDirty,
      contractCount: nextContracts.length,
      updatedAt: new Date().toISOString(),
    };
    // Append-only — but if the engagement was calculated, adding a slot
    // invalidates the cached results.
    updated = transitionToStale(updated);
    await historyStorage.saveEngagement(updated);
    setEngagement(updated);
    setSelected(new Set());
    const newIdx = nextContracts.length - 1;
    navigate(`/engagements/${engagement.id}/contracts/${newIdx}`);
  }

  function toggleRow(idx: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  function toggleAll(checked: boolean) {
    if (!engagement) return;
    setSelected(
      checked ? new Set(engagement.contracts.map((_, i) => i)) : new Set(),
    );
  }

  async function handleBulkDelete() {
    if (!engagement || selected.size === 0) return;
    setBulkDeleteOpen(false);
    // Descending order so each splice leaves the remaining (lower)
    // indices valid. Reuses the existing per-slot hard-delete storage
    // path — no new bulk storage method.
    const indices = Array.from(selected).sort((a, b) => b - a);
    const count = indices.length;
    try {
      let current = engagement;
      for (const idx of indices) {
        current = await historyStorage.deleteContract(current.id, idx);
      }
      setEngagement(current);
      setSelected(new Set());
      toast.success(
        t("engagement.contract.bulkDelete.toast", { count }),
      );
    } catch (err) {
      console.error("[contracts] bulk delete failed", err);
      const refreshed = await historyStorage.getEngagement(engagement.id);
      if (refreshed) setEngagement(refreshed);
      setSelected(new Set());
    }
  }

  async function handleHardDelete(slotIndex: number) {
    if (!engagement) return;
    setPendingDeleteIndex(null);
    try {
      const updated = await historyStorage.deleteContract(
        engagement.id,
        slotIndex,
      );
      setEngagement(updated);
      setSelected(new Set());
      toast.success(t("engagement.contract.delete.toast"));
    } catch (err) {
      console.error("[contracts] deleteContract failed", err);
    }
  }

  // ── Phase 6a.11 — calculate flow + nav guard ─────────────────────────
  const calc = useCalculateEngagement(id ?? "");

  async function handleCalculate() {
    if (!engagement) return;
    await calc.calculate();
    // The hook either navigates to /results (success) or to /contracts
    // (400 with per-slot rejections). Re-load engagement state so the
    // rejection banner reflects the freshly persisted lastCalculateError.
    const refreshed = await historyStorage.getEngagement(engagement.id);
    if (refreshed) setEngagement(refreshed);
  }

  // Navigation guard (Spec §21.7.9.3). `useBlocker` requires a data
  // router and the app uses `<BrowserRouter>` — see deviation note in
  // the closing report. Fallback: popstate listener with `confirm`.
  React.useEffect(() => {
    if (!calc.isCalculating) return;
    const onBeforePop = (e: PopStateEvent) => {
      const ok = window.confirm(t("engagement.calculate.navAbortConfirm"));
      if (ok) {
        calc.cancel();
      } else {
        // Push the current location back so the URL doesn't change.
        window.history.pushState(null, "", window.location.href);
        e.preventDefault();
      }
    };
    // Prime the history stack with a sentinel entry so popstate fires
    // before the actual navigation if the user hits back.
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onBeforePop);
    return () => {
      window.removeEventListener("popstate", onBeforePop);
    };
  }, [calc, t]);

  if (loadState === "loading" || !engagement) {
    return (
      <PageContainer variant="data">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </PageContainer>
    );
  }

  // Phase 6a.12/F-2: enforce the cut-off principle. A contract that
  // lies entirely outside the engagement audit period is downgraded to
  // `partial`, which keeps the Calculate button locked until the auditor
  // resolves the timing mismatch.
  const auditPeriod = {
    start: engagement.auditPeriodStart,
    end: engagement.auditPeriodEnd,
  };
  // Phase 6e.4.2-hotfix-2 — soft-delete is gone (Bug D). The contracts
  // array is a true list of live slots again; no skip filter needed.
  const statuses = engagement.contracts.map((c, idx) =>
    deriveContractStatus({
      contract: c ?? null,
      isDirty: engagement.contractsDirty?.[idx] ?? false,
      serverRejection: engagement.lastCalculateError?.[idx],
      auditPeriod,
      functionalCurrency: engagement.functionalCurrency,
    }),
  );
  const visibleSlotCount = engagement.contracts.length;
  const unfilledCount = statuses.filter((s) => s !== "filled").length;
  const canCalculate = visibleSlotCount > 0 && unfilledCount === 0;

  const engagementStatusKey = engagement.status ?? "draft";
  const engagementStatusLabel = t(
    `engagement.list.engagementStatus.${engagementStatusKey}` as never,
  );

  const rejectionCount = engagement.lastCalculateError
    ? Object.keys(engagement.lastCalculateError).length
    : 0;

  const allSelected =
    visibleSlotCount > 0 && selected.size === visibleSlotCount;

  const hasAnyDescription = engagement.contracts.some(
    (c) => c?.description?.trim(),
  );

  return (
    <PageContainer variant="data" className="space-y-6">
      {rejectionCount > 0 && (
        <Admonition
          type="danger"
          title={t("engagement.calculate.rejectionBannerTitle")}
        >
          <span data-testid="rejection-banner">
            {t("engagement.calculate.rejectionBanner", {
              count: rejectionCount,
            })}
            {": "}
            {Object.keys(engagement.lastCalculateError ?? {})
              .map((k) => engagement.contracts[Number(k)]?.contractId)
              .filter(Boolean)
              .join(", ")}
          </span>
        </Admonition>
      )}

      {calc.lastNetworkError && (
        <Admonition
          type="warning"
          title={t("engagement.calculate.networkErrorTitle")}
        >
          <div className="flex items-center justify-between gap-3">
            <span data-testid="calculate-network-error">
              {calc.lastNetworkError}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                calc.clearError();
                void handleCalculate();
              }}
            >
              {t("engagement.calculate.retry")}
            </Button>
          </div>
        </Admonition>
      )}

      <CalculateLoadingOverlay
        open={calc.isCalculating}
        onCancel={calc.cancel}
      />

      {/* SPEC-SHARE-B1 — Excel import is a write surface; hidden for viewers. */}
      {!readOnly && (
        <ContractsImportSection
          engagement={engagement}
          onEngagementUpdated={setEngagement}
        />
      )}

      {/* Phase 6e.4.2 — TCMB sync section relocated from the
          engagement form. The Magic Button + audit accordion live
          on the contract list page where the contracts they sync
          actually live. SPEC-SHARE-B1: hidden for viewers (they don't
          sync). */}
      {!readOnly && (
        <section
          className="space-y-3"
          data-testid="contract-list-fx-sync-section"
        >
          <MagicSyncButton
            engagement={engagement}
            onEngagementUpdated={setEngagement}
            onPlanComputed={setLatestFxSyncPlan}
          />
          <FxSyncAuditAccordion
            engagement={engagement}
            contractUpdates={latestFxSyncPlan?.contractUpdates}
          />
        </section>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>{engagement.clientName}</CardTitle>
              <div className="mt-1 text-sm text-muted-foreground">
                {formatIsoToDisplay(engagement.auditPeriodStart)} —{" "}
                {formatIsoToDisplay(engagement.auditPeriodEnd)}
                <span className="mx-2">·</span>
                <span className="font-medium text-foreground">
                  {engagement.functionalCurrency}
                </span>
              </div>
            </div>
            <Badge
              variant={engagementStatusKey === "stale" ? "destructive" : "secondary"}
              data-testid="engagement-status-badge"
            >
              {engagementStatusLabel}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {visibleSlotCount === 0 ? (
            <Admonition type="info">
              {t("engagement.list.emptyHint")}
            </Admonition>
          ) : (
            <>
              {selected.size > 0 && (
                <div
                  className="mb-3 flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2"
                  data-testid="contract-bulk-bar"
                >
                  <span className="text-sm font-medium">
                    {t("engagement.contract.bulkDelete.selectedCount", {
                      count: selected.size,
                    })}
                  </span>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDeleteOpen(true)}
                    data-testid="contract-bulk-delete-button"
                  >
                    <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
                    {t("engagement.contract.bulkDelete.button")}
                  </Button>
                </div>
              )}
              <div className="rounded-md border">
                <Table data-testid="contract-list-table">
                  <TableHeader>
                    <TableRow>
                      {/* SPEC-SHARE-B1 — bulk-select column hidden for viewers. */}
                      {!readOnly && (
                        <TableHead className="w-px pr-0">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={(c) => toggleAll(c === true)}
                            aria-label={t(
                              "engagement.contract.bulkDelete.selectAll",
                            )}
                            data-testid="contract-select-all"
                          />
                        </TableHead>
                      )}
                      <TableHead>
                        {t("engagement.list.table.contractId")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("engagement.list.table.rent")}
                      </TableHead>
                      <TableHead>
                        {t("engagement.list.table.currency")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("engagement.list.table.discountRate")}
                      </TableHead>
                      <TableHead>
                        {t("engagement.list.table.paymentFrequency")}
                      </TableHead>
                      {hasAnyDescription && (
                        <TableHead>
                          {t("engagement.list.table.description")}
                        </TableHead>
                      )}
                      <TableHead className="text-right">
                        {t("engagement.list.table.actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {engagement.contracts.map((c, idx) => {
                      const slotContract = c ?? null;
                      const slotDisplayName = displayNameFor(
                        slotContract,
                        t("engagement.list.emptySlotName"),
                      );
                      const isSelected = selected.has(idx);
                      return (
                        <TableRow
                          key={idx}
                          data-testid={`contract-list-row-${idx + 1}`}
                          data-state={isSelected ? "selected" : undefined}
                        >
                          {/* SPEC-SHARE-B1 — per-row select hidden for viewers. */}
                          {!readOnly && (
                            <TableCell className="pr-0">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleRow(idx)}
                                aria-label={t(
                                  "engagement.contract.bulkDelete.selectRow",
                                  { index: idx + 1, name: slotDisplayName },
                                )}
                                data-testid={`contract-row-${idx}-select`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium">
                            <span className="inline-flex items-center gap-2">
                              <ContractStatusDot
                                contractIndex={idx + 1}
                                contract={slotContract}
                                isDirty={
                                  engagement.contractsDirty?.[idx] ?? false
                                }
                                isCurrent={false}
                                serverRejection={
                                  engagement.lastCalculateError?.[idx]
                                }
                                auditPeriod={auditPeriod}
                                functionalCurrency={
                                  engagement.functionalCurrency
                                }
                                onClick={(targetIdx) =>
                                  navigate(
                                    `/engagements/${engagement.id}/contracts/${targetIdx}`,
                                  )
                                }
                              />
                              {slotContract?.contractId?.trim() ||
                                t("engagement.list.emptySlotName")}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums">
                            {slotContract?.payment
                              ? formatNumber(slotContract.payment.amount, 2)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {slotContract?.currency ??
                              engagement.functionalCurrency}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums">
                            {slotContract
                              ? formatPercent(slotContract.discountRateAnnual)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {slotContract?.payment
                              ? (t(
                                  `paymentFrequency.${slotContract.payment.frequency}` as never,
                                ) as string)
                              : "—"}
                          </TableCell>
                          {hasAnyDescription && (
                            <TableCell className="text-muted-foreground">
                              {slotContract?.description?.trim() || "—"}
                            </TableCell>
                          )}
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* SPEC-SHARE-B1 — Edit stays enabled (navigates
                                  to the read-only detail in B2) but relabels to
                                  "Görüntüle" for viewers. */}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    `/engagements/${engagement.id}/contracts/${idx}`,
                                  )
                                }
                                data-testid={`contract-row-${idx}-edit`}
                              >
                                <Pencil
                                  className="h-4 w-4 mr-1"
                                  aria-hidden="true"
                                />
                                {readOnly
                                  ? t("engagement.list.viewContract")
                                  : t("common.edit")}
                              </Button>
                              {/* SPEC-SHARE-B1 — delete hidden for viewers. */}
                              {!readOnly && (
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => setPendingDeleteIndex(idx)}
                                  data-testid={`contract-row-${idx}-delete`}
                                  aria-label={t(
                                    "engagement.contract.delete.buttonAriaLabel",
                                    {
                                      index: idx + 1,
                                      name: slotDisplayName,
                                    },
                                  )}
                                >
                                  <Trash2
                                    className="h-4 w-4 mr-1"
                                    aria-hidden="true"
                                  />
                                  <span className="hidden sm:inline">
                                    {t("engagement.contract.delete.button")}
                                  </span>
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* SPEC-SHARE-B1 — add-contract disabled for viewers, with a tooltip. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddContract}
                disabled={readOnly}
                data-testid="add-contract-button"
              >
                <Plus className="h-4 w-4 mr-1" />
                {t("engagement.contract.navigation.addContract")}
              </Button>
            </span>
          </TooltipTrigger>
          {readOnly && (
            <TooltipContent>
              {t("engagement.list.viewerNoEdit")}
            </TooltipContent>
          )}
        </Tooltip>

        {canCalculate && !readOnly ? (
          <Button
            type="button"
            onClick={handleCalculate}
            data-testid="calculate-button"
          >
            {t("engagement.list.calculateButton")}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button
                  type="button"
                  disabled
                  data-testid="calculate-button"
                  data-disabled-reason={
                    readOnly
                      ? "readonly"
                      : visibleSlotCount === 0
                        ? "no-contracts"
                        : "unfilled"
                  }
                >
                  {t("engagement.list.calculateButton")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {readOnly
                ? t("engagement.list.viewerNoEdit")
                : visibleSlotCount === 0
                  ? t("engagement.list.calculateNoContracts")
                  : t("engagement.list.calculateDisabled", {
                      count: unfilledCount,
                    })}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <DeleteConfirmDialog
        engagement={engagement}
        pendingDeleteIndex={pendingDeleteIndex}
        onCancel={() => setPendingDeleteIndex(null)}
        onConfirm={() => {
          if (pendingDeleteIndex !== null) {
            void handleHardDelete(pendingDeleteIndex);
          }
        }}
      />

      <Dialog
        open={bulkDeleteOpen}
        onOpenChange={(v) => !v && setBulkDeleteOpen(false)}
      >
        <DialogContent data-testid="contract-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>
              {t("engagement.contract.bulkDelete.confirmTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("engagement.contract.bulkDelete.confirmDescription", {
                count: selected.size,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDeleteOpen(false)}
              data-testid="contract-bulk-delete-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleBulkDelete()}
              data-testid="contract-bulk-delete-confirm"
            >
              {t("engagement.contract.bulkDelete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
